import { useState, useRef, useCallback, useEffect } from 'react';
import {
  isSpeechRecognitionAvailable,
  createSpeechRecognition,
  startMediaRecorder,
  transcribeWithWhisper,
} from '../services/voiceService';

const PERM_KEY = 'voiceMicPermission';
const SILENCE_MS = 2000;

/**
 * useVoiceInput
 *
 * Manages microphone permission, Web Speech API, and Whisper fallback.
 *
 * @param {object}   opts
 * @param {function} opts.onTranscript         — (finalText: string) => void
 * @param {function} [opts.onInterimTranscript] — (interimText: string) => void
 *
 * @returns {{
 *   voiceState: 'idle'|'listening'|'processing'|'error',
 *   permissionState: 'unknown'|'granted'|'denied',
 *   interimTranscript: string,
 *   startListening: () => void,
 *   stopListening: () => void,
 *   requestPermission: () => Promise<void>,
 * }}
 */
export default function useVoiceInput({ onTranscript, onInterimTranscript }) {
  const [voiceState, setVoiceState] = useState('idle');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [permissionState, setPermissionState] = useState(() => {
    return localStorage.getItem(PERM_KEY) || 'unknown';
  });

  const recognitionRef = useRef(null);
  const recorderRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const isListeningRef = useRef(false);

  // Persist permission state
  function cachePermission(state) {
    setPermissionState(state);
    if (state === 'granted' || state === 'denied') {
      localStorage.setItem(PERM_KEY, state);
    }
  }

  function resetSilenceTimer() {
    clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = setTimeout(() => {
      if (isListeningRef.current) stopListening();
    }, SILENCE_MS);
  }

  const stopListening = useCallback(() => {
    isListeningRef.current = false;
    clearTimeout(silenceTimerRef.current);

    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* already stopped */ }
      recognitionRef.current = null;
    }

    if (recorderRef.current) {
      // Whisper path — stop() returns Promise<Blob>
      setVoiceState('processing');
      recorderRef.current.stop().then((blob) => {
        return transcribeWithWhisper(blob);
      }).then((text) => {
        setInterimTranscript('');
        setVoiceState('idle');
        if (text && onTranscript) onTranscript(text);
      }).catch(() => {
        setVoiceState('error');
        setTimeout(() => setVoiceState('idle'), 2000);
      });
      recorderRef.current = null;
    } else {
      setVoiceState('idle');
      setInterimTranscript('');
    }
  }, [onTranscript]);

  const startListening = useCallback(async () => {
    if (isListeningRef.current) {
      stopListening();
      return;
    }

    if (isSpeechRecognitionAvailable()) {
      // ── Web Speech API path ──────────────────────────────────────────────────
      const recognition = createSpeechRecognition({
        lang: 'en-GB',
        onInterim: (text) => {
          setInterimTranscript(text);
          if (onInterimTranscript) onInterimTranscript(text);
          resetSilenceTimer();
        },
        onResult: (text) => {
          // continuous mode: each finalized segment accumulates upstream and arrives
          // here as the full transcript so far. Keep listening and let the silence
          // timer decide when to stop — just refresh the input and reset that timer.
          setInterimTranscript(text);
          if (onTranscript) onTranscript(text);
          resetSilenceTimer();
        },
        onEnd: () => {
          if (isListeningRef.current) {
            // ended without a final result (silence / no speech)
            isListeningRef.current = false;
            recognitionRef.current = null;
            setInterimTranscript('');
            setVoiceState('idle');
          }
        },
        onError: (err) => {
          isListeningRef.current = false;
          recognitionRef.current = null;
          setInterimTranscript('');
          if (err !== 'aborted') {
            setVoiceState('error');
            setTimeout(() => setVoiceState('idle'), 2000);
          } else {
            setVoiceState('idle');
          }
        },
      });

      if (!recognition) {
        setVoiceState('error');
        setTimeout(() => setVoiceState('idle'), 2000);
        return;
      }

      recognitionRef.current = recognition;
      isListeningRef.current = true;
      setVoiceState('listening');
      resetSilenceTimer();

      try {
        recognition.start();
      } catch {
        isListeningRef.current = false;
        recognitionRef.current = null;
        setVoiceState('error');
        setTimeout(() => setVoiceState('idle'), 2000);
      }
    } else {
      // ── MediaRecorder / Whisper fallback path ─────────────────────────────────
      try {
        const recorder = await startMediaRecorder();
        recorderRef.current = recorder;
        isListeningRef.current = true;
        setVoiceState('listening');
        resetSilenceTimer();
      } catch {
        setVoiceState('error');
        cachePermission('denied');
        setTimeout(() => setVoiceState('idle'), 2000);
      }
    }
  }, [onTranscript, onInterimTranscript, stopListening]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Explicitly request microphone permission (called from the inline banner).
   * Uses getUserMedia to trigger the browser prompt.
   */
  const requestPermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      cachePermission('granted');
    } catch {
      cachePermission('denied');
    }
  }, []);

  // Sync permission with browser Permissions API if available
  useEffect(() => {
    if (!navigator.permissions) return;
    navigator.permissions.query({ name: 'microphone' }).then((status) => {
      if (status.state === 'granted') cachePermission('granted');
      if (status.state === 'denied') cachePermission('denied');

      status.onchange = () => {
        if (status.state === 'granted') cachePermission('granted');
        if (status.state === 'denied') cachePermission('denied');
      };
    }).catch(() => { /* permissions API not supported */ });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Clean up on unmount
  useEffect(() => {
    return () => {
      clearTimeout(silenceTimerRef.current);
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch { /* ignore */ }
      }
    };
  }, []);

  return {
    voiceState,
    permissionState,
    interimTranscript,
    startListening,
    stopListening,
    requestPermission,
  };
}
